import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import EditableItemList from './common/EditableItemList';
import RichTextEditor from '../../../common/RichTextEditor';
import { toPickerValue, fromPickerValue } from '../../../../utils/dateOnly';
import { useCV } from '../contexts/CVContext';
import { formatDateRange } from '../helpers/ProcessTemplate';
import { cvManagementApi } from '../../../../services/api/cvManagementApi';

const Education = () => {
  const { isDataReady, cvData, updateEducation, readonly } = useCV();
  
  const [educationDegrees, setEducationDegrees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Initialize with empty education list
  const [educationList, setEducationList] = useState([]);
  
  const [expandedIndex, setExpandedIndex] = useState(null);

  // Fetch education degrees
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await cvManagementApi.getAllEducationDegrees();
        setEducationDegrees(response || []);
      } catch (error) {
        console.error('Error fetching education degrees:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Update local state when context data changes, but only on initial load
  useEffect(() => {
    if (cvData.education?.length > 0 && isDataReady) {
      setEducationList(cvData.education);
    }
  }, [cvData.education, isDataReady]);
  
  // Custom handler to update both local state and context
  const handleEducationChange = (newEducationList) => {
    // Update the order property for each education entry
    const orderedEducationList = newEducationList.map((edu, index) => ({
      ...edu,
      order: index + 1
    }));
    
    setEducationList(orderedEducationList);
    updateEducation(orderedEducationList);
  };

  // Function to create a new empty education entry
  const createNewEducation = () => ({
    id: null,
    institution: '', 
    educationDegree: null,
    city: '',
    country: '',
    startDate: null, 
    endDate: null,
    description: '',
    inProgress: false,
    order: educationList.length + 1
  });

  // Function to render the summary of an education entry (collapsed view)
  const renderEducationSummary = (education) => (
    <>
      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
        {education.educationDegree?.name || '(Not specified)'} {education.institution ? `at ${education.institution}` : ''}
      </Typography>
      {(education.city || education.country) && (
        <Typography variant="body2" color="text.secondary">
          {[education.city, education.country].filter(Boolean).join(', ')}
        </Typography>
      )}
      {(education.startDate || education.endDate || education.inProgress) && (
        <Typography variant="body2" color="text.secondary">
          {formatDateRange(education.startDate, education.endDate, education.inProgress)}
        </Typography>
      )}
    </>
  );

  // Function to render the edit form for an education entry (expanded view)
  const renderEducationEditForm = (education, onChange) => (
      <Grid 
        container 
        spacing={2}
        sx={{
          width: '100%',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)'
          },
          gap: '16px',
          '& .MuiGrid-item': {
            padding: '0',
            width: '100%'
          }
        }}
      >
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Institution <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={education.institution || ''}
              onChange={(e) => onChange({ ...education, institution: e.target.value })}
              placeholder="Enter the institution name"
              size="small"
              autoComplete="new-password"
              name={`cv_institution_${Math.random().toString(36).substring(2, 9)}`}
              required
              disabled={readonly}
            />
          </Box>
        </Grid>
        
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Degree <span style={{ color: 'red' }}>*</span>
            </Typography>
            <FormControl fullWidth size="small" required>
              <Select
                value={education.educationDegree?.id || ''}
                onChange={(e) => {
                  const selectedDegree = educationDegrees.find(degree => degree.id === e.target.value);
                  onChange({
                    ...education,
                    educationDegree: {
                      id: selectedDegree.id,
                      name: selectedDegree.name
                    }
                  });
                }}
                displayEmpty
                disabled={readonly}
                sx={{ backgroundColor: 'white' }}
              >
                <MenuItem value="" disabled>
                  <em>Select degree</em>
                </MenuItem>
                {educationDegrees.map((degree) => (
                  <MenuItem key={degree.id} value={degree.id}>
                    {degree.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
        
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              City
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={education.city || ''}
              onChange={(e) => onChange({ ...education, city: e.target.value })}
              placeholder="Enter city"
              size="small"
              autoComplete="new-password"
              name={`cv_edu_city_${Math.random().toString(36).substring(2, 9)}`}
              disabled={readonly}
            />
          </Box>
        </Grid>
        
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Country
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={education.country || ''}
              onChange={(e) => onChange({ ...education, country: e.target.value })}
              placeholder="Enter country"
              size="small"
              autoComplete="new-password"
              name={`cv_edu_country_${Math.random().toString(36).substring(2, 9)}`}
              disabled={readonly}
            />
          </Box>
        </Grid>
        
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Start Date <span style={{ color: 'red' }}>*</span>
            </Typography>
            <DatePicker
              views={['year', 'month']}
              value={toPickerValue(education.startDate)}
              onChange={(date) => onChange({ ...education, startDate: fromPickerValue(date) })}
              disabled={readonly}
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  size: "small", 
                  placeholder: "MM / YYYY",
                  required: true
                } 
              }}
            />
          </Box>
        </Grid>
        
        <Grid item>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              End Date
            </Typography>
            <DatePicker
              views={['year', 'month']}
              value={toPickerValue(education.endDate)}
              onChange={(date) => onChange({ ...education, endDate: fromPickerValue(date) })}
              disabled={education.inProgress || readonly}
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  size: "small", 
                  placeholder: "MM / YYYY" 
                } 
              }}
            />
            <FormControlLabel
              control={
                <Checkbox 
                  checked={education.inProgress || false} 
                  onChange={(e) => {
                    const updatedEducation = { 
                      ...education, 
                      inProgress: e.target.checked,
                      endDate: e.target.checked ? null : education.endDate 
                    };
                    onChange(updatedEducation);
                  }}
                  size="small"
                  disabled={readonly}
                />
              }
              label="In Progress"
              sx={{ mt: 1 }}
            />
          </Box>
        </Grid>
        
        <Grid item sx={{ gridColumn: { md: '1 / span 2' } }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Description
            </Typography>
            <RichTextEditor
              initialContent={education.description || ''}
              onChange={(content) => onChange({ ...education, description: content })}
              description="Add details about your studies, achievements, thesis, etc."
              readonly={readonly}
            />
          </Box>
        </Grid>
      </Grid>
  );

  return (
    <EditableItemList
      items={educationList}
      setItems={handleEducationChange}
      expandedIndex={expandedIndex}
      setExpandedIndex={setExpandedIndex}
      createNewItem={createNewEducation}
      renderSummary={renderEducationSummary}
      renderEditForm={renderEducationEditForm}
      addButtonText={educationList.length > 0 ? "Add one more education" : "Add an education"}
      emptyState={{
        title: "You haven't added any education yet.",
        description: "Add any relevant institution you've attended."
      }}
      itemName="education"
      isLoading={isLoading}
      disabled={isLoading}
      readonly={readonly}
    />
  );
};

export default Education; 